import Selects from "./selects";

/**
 * Add and remove rows in the influencer social links editor, without a round trip.
 *
 * PROGRESSIVE ENHANCEMENT, NOT THE MECHANISM. The Add and Remove controls are real submit buttons
 * and the server handles them on its own — SubmitSocialLinks inspects `add` / `remove` and
 * re-renders. All this does is intercept those clicks so the common case is instant. With scripting
 * off the editor still works, exactly as the cover-image removal form on this page does.
 *
 * Two things here are fiddlier than they look, and both caused real bugs:
 *
 *   1. asp-for emits TWO indexed attributes per field in DIFFERENT formats — name="Links[0].Value"
 *      and id="Links_0__Value". Renumbering only the bracketed one leaves every cloned row sharing
 *      row 0's ids, which breaks label association and anything that looks a field up by id.
 *
 *   2. EVERY select on this site is replaced by a SlimSelect widget (see components/selects.js),
 *      which hides the native <select> and renders its own .ss-main markup beside it. Cloning a
 *      live row therefore cloned that widget too — dead markup with no instance driving it, still
 *      showing the platform it was copied from and refusing every click. So new rows are cloned
 *      from a pristine <template> the server renders, never from a row on screen, and Selects() is
 *      re-run afterwards to give the new native select a widget of its own. Selects() skips
 *      anything already wrapped, so re-running it is safe.
 *
 * The platform rules and every message come from data attributes the server rendered, so the
 * pattern the browser enforces IS the pattern the server enforces and the wording is the same
 * dictionary entry. A field that looks fine while typing and is refused on save is worse than no
 * client validation at all.
 */

/** Attributes that carry a row index and therefore have to be renumbered. */
const INDEXED_ATTRIBUTES = ["name", "id", "for", "data-valmsg-for", "aria-describedby"];

/** `Links[0].Value` — the posted name. */
const BRACKET_INDEX = /\[\d+\]/;

/** `Links_0__Value` — the id asp-for derives from that same expression. */
const UNDERSCORE_INDEX = /_\d+__/;

function renumber(value, index) {
  if (BRACKET_INDEX.test(value)) return value.replace(BRACKET_INDEX, `[${index}]`);
  if (UNDERSCORE_INDEX.test(value)) return value.replace(UNDERSCORE_INDEX, `_${index}__`);
  return value;
}

function reindex(list) {
  list.querySelectorAll(".js-social-link-rows__row").forEach((row, index) => {
    row.querySelectorAll("[name], [id], [for], [data-valmsg-for], [aria-describedby]").forEach((element) => {
      INDEXED_ATTRIBUTES.forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (value) element.setAttribute(attribute, renumber(value, index));
      });
    });

    // The remove button posts its own index in the no-JS path, so it has to stay correct.
    const remove = row.querySelector(".js-social-link-rows__remove");
    if (remove) remove.value = String(index);
  });
}

/** Guards against the re-entrancy of updating a widget from inside its own change event. */
let syncing = false;

function platformRules(container) {
  try {
    return JSON.parse(container.dataset.platforms || "[]");
  } catch {
    return [];
  }
}

function ruleFor(container, key) {
  return key ? platformRules(container).find((rule) => rule.key === key) ?? null : null;
}

function rowParts(row) {
  return {
    select: row.querySelector("select"),
    input: row.querySelector('input[type="text"]'),
    message: row.querySelector(".js-social-link-rows__message")
  };
}

function setMessage(row, text) {
  const { message } = rowParts(row);
  if (!message) return;

  message.textContent = text || "";
  message.hidden = !text;

  // The server's message for this row described the value as it was POSTED. The moment the member
  // touches the row it is stale, and leaving it beside a live one is just two contradictory
  // sentences.
  row.querySelectorAll("[data-valmsg-for]").forEach((stale) => {
    stale.textContent = "";
  });
}

/**
 * Greys out platforms already used on another row, in the native select AND in the SlimSelect
 * widget standing in front of it.
 *
 * Stopping the choice being made beats letting it be made and silently dropping the loser on save,
 * which is what the server does with a duplicate.
 */
function syncAvailability(container, list) {
  if (syncing) return;

  const rows = Array.from(list.querySelectorAll(".js-social-link-rows__row"));
  const taken = new Set(rows.map((row) => rowParts(row).select?.value).filter(Boolean));

  syncing = true;

  try {
    rows.forEach((row) => {
      const { select } = rowParts(row);
      if (!select) return;

      const own = select.value;
      const isBlocked = (value) => value !== "" && value !== own && taken.has(value);

      Array.from(select.options).forEach((option) => {
        option.disabled = isBlocked(option.value);
      });

      // The widget renders from its own copy of the data, so the native disabled flags above are
      // invisible to it until they are pushed across.
      const slim = select.slim;
      if (!slim) return;

      slim.setData(
        slim.getData().map((option) => ({ ...option, disabled: isBlocked(option.value) }))
      );

      if (own) slim.setSelected(own);
    });
  } finally {
    syncing = false;
  }
}

/**
 * Applies the selected platform's placeholder, and validates what the member typed against that
 * platform's rule.
 */
function syncRow(container, list, row, { validate }) {
  const { select, input } = rowParts(row);
  if (!select || !input) return;

  const rule = ruleFor(container, select.value);

  input.placeholder = rule ? rule.placeholder : container.dataset.msgChoosePlatform || "";

  if (!rule) {
    setMessage(row, "");
    return;
  }

  // Disabling the option stops a duplicate being CHOSEN, but a failed post redisplays the rows
  // exactly as they were submitted — duplicates and all — so the pair still has to be explained.
  const rows = Array.from(list.querySelectorAll(".js-social-link-rows__row"));
  const isLaterDuplicate = rows
    .slice(0, rows.indexOf(row))
    .some((earlier) => rowParts(earlier).select?.value === select.value);

  if (isLaterDuplicate) {
    setMessage(row, (container.dataset.msgDuplicate || "").replace("{0}", rule.label));
    return;
  }

  // Only on blur/change. Rewriting the value on every keystroke fights the member mid-word.
  if (!validate) return;

  const raw = input.value.trim();

  if (raw.length === 0) {
    input.value = "";
    setMessage(row, "");
    return;
  }

  if (rule.isUrl) {
    // Mirrors SocialLinkNormaliser.NormaliseUrl: shown back with the https:// it will be saved with.
    const url = normaliseUrl(raw);
    input.value = url;
    setMessage(row, isValidUrl(url) ? "" : container.dataset.msgInvalidUrl);
    return;
  }

  // Mirrors SocialLinkNormaliser.NormaliseHandle: a pasted profile URL is reduced to its handle,
  // then the platform's own prefix is put back for display.
  const handle = normaliseHandle(raw);
  const valid = handle.length > 0 && new RegExp(rule.pattern).test(handle);

  input.value = handle.length > 0 ? rule.prefix + handle : "";
  setMessage(row, valid ? "" : (container.dataset.msgInvalidHandle || "").replace("{0}", rule.label));
}

/** The client half of SocialLinkNormaliser.NormaliseHandle. */
function normaliseHandle(value) {
  let handle = (value || "").trim();
  if (!handle) return "";

  try {
    const url = new URL(handle);
    const lastSegment = url.pathname.split("/").filter(Boolean).pop();
    if (lastSegment) handle = lastSegment;
  } catch {
    // Not a URL, which is the normal case — leave it as typed.
  }

  return handle.trim().replace(/^@+/, "").replace(/^\/+|\/+$/g, "").trim();
}

/** The client half of SocialLinkNormaliser.NormaliseUrl. */
function normaliseUrl(value) {
  const url = (value || "").trim();
  return !url || url.includes("://") ? url : `https://${url}`;
}

function isValidUrl(value) {
  try {
    const url = new URL(value);
    return (url.protocol === "https:" || url.protocol === "http:")
      && !url.username
      && url.hostname.includes(".");
  } catch {
    return false;
  }
}

/** The clone source: an empty row the server rendered inside an inert <template>. */
function templateRow(container) {
  return container.querySelector(".js-social-link-rows__template")?.content?.firstElementChild ?? null;
}

function countFilled(list) {
  return Array.from(list.querySelectorAll(".js-social-link-rows__row")).filter((row) => {
    const platform = row.querySelector("select");
    const value = row.querySelector('input[type="text"]');
    return platform?.value && value?.value.trim();
  }).length;
}

function refresh(container, list) {
  const maxLinks = parseInt(container.dataset.maxLinks, 10);
  const template = container.dataset.countTemplate;
  const counter = container.querySelector('[data-testid="social-links-count"]');
  const addButton = container.querySelector(".js-social-link-rows__add");
  const rowCount = list.querySelectorAll(".js-social-link-rows__row").length;

  if (counter && template) {
    counter.textContent = template.replace("{0}", countFilled(list)).replace("{1}", maxLinks);
  }

  // AC 3: at the cap there is nothing left to click. The server refuses a longer post regardless.
  if (addButton) addButton.hidden = rowCount >= maxLinks;
}

function addRow(container, list) {
  const rows = list.querySelectorAll(".js-social-link-rows__row");
  const maxLinks = parseInt(container.dataset.maxLinks, 10);
  const template = templateRow(container);

  if (!template || rows.length >= maxLinks) return;

  const clone = template.cloneNode(true);

  list.appendChild(clone);
  reindex(list);

  // The clone carries a bare native <select>. Without this it stays bare while every other select
  // on the page is a styled widget.
  Selects();

  syncAvailability(container, list);
  syncRow(container, list, clone, { validate: false });
  refresh(container, list);
  clone.querySelector("select")?.focus();
}

function removeRow(container, list, row) {
  const rows = list.querySelectorAll(".js-social-link-rows__row");

  if (rows.length <= 1) {
    // Never leave the member with no row at all. Replaced with a fresh template clone rather than
    // blanked in place, because the SlimSelect widget on the old row cannot be reset from the
    // native select underneath it — it would keep showing the platform that was just removed.
    const template = templateRow(container);

    if (template) {
      const fresh = template.cloneNode(true);
      row.replaceWith(fresh);
      reindex(list);
      Selects();
      syncAvailability(container, list);
      syncRow(container, list, fresh, { validate: false });
      refresh(container, list);
      return;
    }
  }

  row.remove();
  reindex(list);
  syncAvailability(container, list);
  refresh(container, list);
}

function SocialLinkRows() {
  const containers = document.querySelectorAll(".js-social-link-rows");

  if (!containers?.length) return;

  containers.forEach((container) => {
    const list = container.querySelector(".js-social-link-rows__list");

    if (!list) return;

    // Delegated, so cloned rows need no wiring of their own.
    container.addEventListener("click", (event) => {
      // closest() from an <svg>/<use> inside the button still resolves, because events inside a
      // use-element's shadow tree retarget to the <use> in the light DOM.
      const target = event.target;

      const addButton = target.closest?.(".js-social-link-rows__add");

      if (addButton && container.contains(addButton)) {
        event.preventDefault();
        addRow(container, list);
        return;
      }

      const removeButton = target.closest?.(".js-social-link-rows__remove");

      if (removeButton && container.contains(removeButton)) {
        event.preventDefault();
        const row = removeButton.closest(".js-social-link-rows__row");
        if (row) removeRow(container, list, row);
      }
    });

    container.addEventListener("change", (event) => {
      if (syncing) return;

      const row = event.target.closest?.(".js-social-link-rows__row");

      if (row) {
        // A platform change frees up whatever it was, and takes whatever it now is.
        if (event.target.tagName === "SELECT") syncAvailability(container, list);

        syncRow(container, list, row, { validate: true });
      }

      refresh(container, list);
    });

    // Keeps the "N of 5" honest as the member types, rather than only after a save.
    container.addEventListener("input", () => refresh(container, list));

    // Validate on the way out of the field. A `change` event only fires when the value actually
    // changed, so leaving an untouched-but-invalid field would otherwise say nothing.
    container.addEventListener("focusout", (event) => {
      if (syncing || event.target.tagName !== "INPUT") return;

      const row = event.target.closest?.(".js-social-link-rows__row");
      if (row) syncRow(container, list, row, { validate: true });
    });

    syncAvailability(container, list);
    list.querySelectorAll(".js-social-link-rows__row").forEach((row) => {
      syncRow(container, list, row, { validate: false });
    });

    refresh(container, list);
  });
}

function initSocialLinkRows() {
  if (document.readyState !== "loading") {
    SocialLinkRows();
  } else {
    document.addEventListener("DOMContentLoaded", SocialLinkRows);
  }
}

export default initSocialLinkRows;
